import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { Service, Window } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDynoWs } from "@/hooks/useDynoWs";
import { useDynoMqtt, isMqttV2Enabled } from "@/hooks/useDynoMqtt";
import { DashboardTiles } from "@/components/DashboardTiles";
import { TotalLoadSection } from "@/components/TotalLoadSection";
import { PqActivity } from "@/components/PqActivity";
import { PqTuningDialog } from "@/components/PqTuningDialog";
import { useLiveFeed } from "@/stores/liveFeed";
import { IS_ADMIN_HOST } from "@/lib/auth";

interface Migration {
  id: number;
  system_id: string;
  service_name: string | null;
  from_node: string;
  to_node: string;
  detected_at: string;
  location: string | null;
}


// Dashboard: grid of tiles for Acuvim services in the selected window.
// Static service metadata comes from /api/services; live V/I/P/PF/F comes from
// the /api/ws/realtime dyno_snapshot stream via useDynoWs + liveFeed store.

export function Dashboard() {
  const [sp, setSp] = useSearchParams();
  const windowId = sp.get("window");
  const navigate = useNavigate();

  // A2.2 — gate realtime source by ?mqtt_v2=1 / localStorage.mqtt_v2.
  // When MQTT is enabled, it drives liveFeed; when MQTT trips its
  // failure threshold (3 token-fetch fails or 10s no broker response),
  // the WS path takes over automatically without a page reload.
  // Only one hook ever subscribes at a time — no parallel realtime streams.
  const mqttV2Wanted = isMqttV2Enabled();
  const mqttHook = useDynoMqtt({ enabled: mqttV2Wanted });
  const useWsFallback = !mqttV2Wanted || mqttHook.mqttFailed;
  useDynoWs({ enabled: useWsFallback });

  // Tri-state chip: connecting / live (with backup hint) / waiting.
  const storeConnected = useLiveFeed((s) => s.connected);
  const liveActive = useWsFallback ? storeConnected : mqttHook.mqttConnected;
  const liveConnecting = !useWsFallback && mqttHook.mqttConnecting;
  const mqttBackup = mqttV2Wanted && mqttHook.mqttFailed;

  const [windows, setWindows] = useState<Window[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [migrations, setMigrations] = useState<Migration[]>([]);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);

  // Poll for recent meter migrations (admin-only). Rolling 24h window,
  // scoped to the SELECTED event: the server only returns moves for meters
  // that are active members of this window, and collapses a flapping meter
  // to a single prompt. Server filters out confirmed migrations; the client
  // just renders what comes back.
  useEffect(() => {
    if (!IS_ADMIN_HOST) return;
    if (!windowId) {
      setMigrations([]);
      return;
    }
    let cancelled = false;
    const fetchOnce = () => {
      api
        .get<{ migrations: Migration[] }>(
          `/api/dyno/migrations?hours=24&window_id=${encodeURIComponent(windowId)}`,
        )
        .then((r) => !cancelled && setMigrations(r.migrations))
        .catch(() => {
          /* non-blocking */
        });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [windowId]);

  async function confirmMigration(m: Migration) {
    setConfirmingId(m.id);
    try {
      const r = await api.post<{ stale_rows_removed: number }>(
        `/api/dyno/migrations/${m.id}/confirm`,
        {}
      );
      // Remove every banner for this meter immediately — the server confirms
      // all open rows for the system_id, so the flapping siblings are gone too.
      setMigrations((prev) => prev.filter((x) => x.system_id !== m.system_id));
      // Re-fetch services so the dashboard reflects the cleanup (stale row gone).
      if (windowId) {
        try {
          const fresh = await api.get<{ services: Service[] }>(
            `/api/services?window_id=${encodeURIComponent(windowId)}`
          );
          setServices(fresh.services);
        } catch {
          /* non-blocking */
        }
      }
      // Quiet no-op feedback unless something noteworthy
      if (r.stale_rows_removed === 0) {
        // Already cleaned up via the freshest-wins dedup elsewhere — fine.
      }
    } catch (e) {
      alert(`Confirm failed: ${e}`);
    } finally {
      setConfirmingId(null);
    }
  }

  const visibleMigrations = migrations;

  // The selected event's membership system_ids — the authoritative list the
  // tiles render from. Total Show Load scopes its live WS sum to these (the
  // admin WS feed carries every event's services). Memoized so the realtime
  // hook's reduction isn't re-keyed on every render.
  const eventSystemIds = useMemo(
    () => new Set(services.map((s) => s.system_id)),
    [services],
  );

  // Restore the dashboard scroll position after returning from a tile's
  // detail view. The position is saved on tile-open (onOpen below); a route
  // round-trip remounts the dashboard and refetches, so scroll resets to the
  // top — re-apply the saved offset once the tiles have rendered. One-shot
  // per mount, and consumed (removed) so a fresh load still starts at the top.
  const dashScrollRestored = useRef(false);
  useEffect(() => {
    if (dashScrollRestored.current) return;
    if (loading || services.length === 0) return;
    const saved = sessionStorage.getItem("dyno:dashboardScrollY");
    if (saved == null) return;
    dashScrollRestored.current = true;
    sessionStorage.removeItem("dyno:dashboardScrollY");
    const y = Number(saved);
    if (Number.isFinite(y) && y > 0) {
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [loading, services]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ windows: Window[] }>("/api/windows?include_closed=false")
      .then((r) => {
        if (cancelled) return;
        setWindows(r.windows);
        // Pick the first window when none is selected — or when the selected
        // one isn't in the list. The dropdown only lists OPEN events, so a
        // stale ?window= pointing at a since-closed event can't be fixed by
        // re-selecting; without this guard the feed silently queries the
        // wrong (closed) window and shows no systems.
        const missing =
          !!windowId && !r.windows.some((w) => w.window_id === windowId);
        if ((!windowId || missing) && r.windows.length > 0) {
          const sp2 = new URLSearchParams(sp);
          sp2.set("window", r.windows[0].window_id);
          setSp(sp2, { replace: true });
        }
      })
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!windowId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<{ services: Service[] }>(
        `/api/services?window_id=${encodeURIComponent(windowId)}`
      )
      .then((r) => !cancelled && setServices(r.services))
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [windowId]);

  const activeWindow = windows.find((w) => w.window_id === windowId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Live Feed</h1>
          <p className="text-sm text-muted-foreground">
            {activeWindow ? activeWindow.name : "Select an event to begin"}
            {" · "}
            {liveActive ? (
              <span className="text-emerald-400">
                ● live{mqttBackup ? " (backup)" : ""}
              </span>
            ) : liveConnecting ? (
              <span className="text-amber-400">◌ connecting</span>
            ) : (
              <span className="text-muted-foreground">○ waiting</span>
            )}
          </p>
        </div>
        {windows.length > 0 && (
          <select
            value={windowId ?? ""}
            onChange={(e) => {
              const sp2 = new URLSearchParams(sp);
              sp2.set("window", e.target.value);
              setSp(sp2);
            }}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="" disabled>
              — select event —
            </option>
            {windows.map((w) => (
              <option key={w.window_id} value={w.window_id}>
                {w.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <div className="text-destructive text-sm">Error: {error}</div>}
      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      <TotalLoadSection windowId={windowId} eventSystemIds={eventSystemIds} />

      {visibleMigrations.length > 0 && (
        <div className="space-y-2">
          {visibleMigrations.map((m) => {
            const label = m.service_name ?? m.location ?? m.system_id;
            const detected = new Date(m.detected_at).toLocaleString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            });
            return (
              <Card key={m.id} className="border-amber-500/40 bg-amber-500/5">
                <CardContent className="py-3 flex items-center justify-between gap-4">
                  <div className="text-sm">
                    <span className="font-medium">{m.system_id}</span> moved{" "}
                    <span className="font-mono text-xs">{m.from_node}</span> →{" "}
                    <span className="font-mono text-xs">{m.to_node}</span>{" "}
                    <span className="text-muted-foreground">at {detected}</span>
                    .{" "}
                    {m.service_name ? (
                      <>Still <span className="font-medium">"{label}"</span>?</>
                    ) : (
                      <>Same physical meter? Confirm to dismiss.</>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={confirmingId === m.id}
                      onClick={() => confirmMigration(m)}
                    >
                      {confirmingId === m.id ? "Confirming…" : "Yes, same meter"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        navigate(`/admin/load-in?window=${windowId ?? ""}`)
                      }
                    >
                      Rename
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {windowId && !loading && services.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No Acuvim services in this event yet.
          </CardContent>
        </Card>
      )}

      {windowId && services.length > 0 && (
        <DashboardTiles
          services={services}
          windowId={windowId}
          onOpen={(systemId) => {
            // Remember the scroll position so the detail view's back button
            // returns the operator to exactly where they were.
            sessionStorage.setItem(
              "dyno:dashboardScrollY",
              String(window.scrollY),
            );
            navigate(
              `/services/${encodeURIComponent(systemId)}?window=${windowId}`,
            );
          }}
        />
      )}

      {windowId && services.length === 0 && (
        <div className="text-xs text-muted-foreground">
          Need to add Acuvim meters?{" "}
          <Button variant="link" className="p-0 h-auto text-xs" onClick={() => navigate(`/admin/load-in?window=${windowId}`)}>
            Open load-in
          </Button>
        </div>
      )}

      {windowId && (
        <div className="space-y-2 pt-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            PQ Activity
          </h2>
          <PqActivity windowId={windowId} />
        </div>
      )}

      {/* PQ-tuning recalibration dialog — driven by pqTuningStore.
          Renders nothing when closed; mounts above all tiles on open. */}
      <PqTuningDialog windowId={windowId ?? undefined} />
    </div>
  );
}
